<script lang="ts" setup>
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import ProgressBar from 'primevue/progressbar'
import { nextTick, inject, onMounted, ref } from 'vue'
import { $fetch } from 'ofetch'

const { t } = useNuxtApp().$i18n
const log = useLogger('component:session-restore-modal')
const supabaseClient = useSupabaseClient()
const businessStore = useBusinessStore()
const dialogRef = inject('dialogRef') as { value: { close: (data?: { data: Record<string, unknown> }) => void } }

const isLoading = ref(false)
const errorMessage = ref('')
const email = ref('')
const password = ref('')

// Try to get current user email for pre-fill
onMounted(async () => {
  try {
    const { data } = await supabaseClient.auth.getUser()
    if (data?.user?.email) {
      email.value = data.user.email
    }
  }
  catch {
    // Silently fail - user will just have to type email
  }
})

function validateForm(): boolean {
  errorMessage.value = ''

  if (!email.value || !email.value.includes('@')) {
    errorMessage.value = t('Please enter a valid email')
    return false
  }

  if (!password.value) {
    errorMessage.value = t('Please provide your password')
    return false
  }

  return true
}

async function handleLogin(): Promise<void> {
  if (!validateForm()) return

  isLoading.value = true
  errorMessage.value = ''

  try {
    const { data, error: authError } = await supabaseClient.auth.signInWithPassword({
      email: email.value,
      password: password.value,
    })

    if (authError) {
      log.error('Auth error:', authError)
      errorMessage.value = t('Invalid email or password')
      isLoading.value = false
      return
    }

    if (data?.session?.user) {
      // Set cookies directly from session
      const { access_token, refresh_token } = data.session

      const accessTokenCookie = useCookie('sb-access-token')
      const refreshTokenCookie = useCookie('sb-refresh-token')

      accessTokenCookie.value = access_token
      refreshTokenCookie.value = refresh_token

      // Ensure cookies are set before API calls
      await nextTick()

      // Re-fetch business context
      try {
        const userBusiness = await $fetch('/api/user/business', { method: 'GET' })

        if (!userBusiness?.business_id) {
          errorMessage.value = t('No business assigned to this account')
          isLoading.value = false
          return
        }

        // Fetch business info
        const businessInfo = await $fetch(`/api/business/${userBusiness.business_id}`, { method: 'GET' })

        if (!businessInfo) {
          errorMessage.value = t('Failed to load business configuration')
          isLoading.value = false
          return
        }

        // Update cookies
        const buuidCookie = useCookie('buuid', {
          maxAge: 7 * 24 * 60 * 60,
          secure: true,
          sameSite: 'strict',
        })
        const serviceTypeCookie = useCookie('service_type', {
          maxAge: 7 * 24 * 60 * 60,
          secure: true,
          sameSite: 'strict',
        })

        buuidCookie.value = userBusiness.business_id
        serviceTypeCookie.value = businessInfo.service_type

        // Update Pinia store
        businessStore.businessInfo = businessInfo

        // Force localStorage write
        if (typeof window !== 'undefined') {
          const existing = JSON.parse(localStorage.getItem('business-store') || '{}')
          localStorage.setItem('business-store', JSON.stringify({ businessInfo, hasMultipleBusinesses: existing.hasMultipleBusinesses }))
        }

        log.info('Session restored successfully')
        useSuccessToast('Session restored')

        // Close modal with success - pass data directly, PrimeVue wraps it in { data: ... }
        dialogRef.value?.close({ success: true })
      }
      catch (businessError) {
        log.error('Failed to restore business context:', businessError)
        errorMessage.value = t('Failed to restore session. Please try again.')
        isLoading.value = false
      }
    }
  }
  catch (error) {
    log.error('Login error:', error)
    errorMessage.value = t('An error occurred. Please try again.')
    isLoading.value = false
  }
}

function handleCancel(): void {
  log.info('User cancelled session restore')
  // Pass data directly - PrimeVue DialogService wraps it in { data: ... }
  dialogRef.value?.close({ cancelled: true })
}
</script>

<template>
  <div class="p-4">
    <!-- Header -->
    <div class="text-center mb-6">
      <div class="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i class="pi pi-lock text-amber-600 text-2xl" />
      </div>
      <h2 class="text-xl font-semibold text-gray-900 m-0">
        {{ t('Your session has expired') }}
      </h2>
      <p class="text-gray-600 mt-2 mb-0">
        {{ t('Please sign in again to continue') }}
      </p>
    </div>

    <!-- Progress bar -->
    <ProgressBar
      v-show="isLoading"
      mode="indeterminate"
      style="height: 6px"
      class="mb-4"
    />

    <!-- Error message -->
    <div
      v-if="errorMessage"
      class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
    >
      <p class="text-red-700 text-sm m-0">
        {{ errorMessage }}
      </p>
    </div>

    <!-- Login form -->
    <form
      class="flex flex-col gap-4"
      @submit.prevent="handleLogin"
    >
      <div class="flex flex-col gap-2">
        <label
          for="session-email"
          class="font-semibold"
        >{{ t('Email') }}</label>
        <InputText
          id="session-email"
          v-model="email"
          type="email"
          :placeholder="t('Enter your email')"
          autocomplete="username"
          class="w-full"
          :disabled="isLoading"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label
          for="session-password"
          class="font-semibold"
        >{{ t('Password') }}</label>
        <Password
          id="session-password"
          v-model="password"
          :placeholder="t('Enter your password')"
          :feedback="false"
          toggle-mask
          autocomplete="current-password"
          class="w-full"
          :input-class="'w-full'"
          :disabled="isLoading"
        />
      </div>

      <div class="flex gap-3 mt-4">
        <Button
          type="button"
          :label="t('Cancel')"
          severity="secondary"
          outlined
          class="flex-1"
          :disabled="isLoading"
          @click="handleCancel"
        />
        <Button
          type="submit"
          :label="t('Sign in to continue')"
          class="flex-1"
          :loading="isLoading"
        />
      </div>
    </form>
  </div>
</template>
